import { CommonModule, Location } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import CountryList from 'country-list-with-dial-code-and-flag'
import CountryFlagSvg from 'country-list-with-dial-code-and-flag/dist/flag-svg'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { NzButtonModule } from 'ng-zorro-antd/button'
import { NzDividerModule } from 'ng-zorro-antd/divider'
import { NzFormLayoutType } from 'ng-zorro-antd/form'
import { NzIconModule } from 'ng-zorro-antd/icon'
import { NzInputModule } from 'ng-zorro-antd/input'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { NzSelectModule } from 'ng-zorro-antd/select'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import { mergeMap } from 'rxjs'
import { AtomModule } from '../../atom/atom.module'
import { AppService } from '../../service/app.service'
import { AuthenticationService } from '../../service/authentication.service'
import { CountryService } from '../../service/country.service'
import { CustomerService } from '../../service/customer.service'
import { OnboardingService } from '../../service/onboarding.service'
import { phoneValidator } from '../../utils/validators/phone-validator'
import { OnboardingProgressState } from '../../gql/generated'

interface Country {
  code: string
  name: string
  dialingCode: string
  flag: SafeHtml
}

@Component({
  selector: 'app-register-complete',
  templateUrl: './register-complete.component.html',
  styleUrl: './register-complete.component.less',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    NzButtonModule,
    NzDividerModule,
    NzInputModule,
    NzSelectModule,
    NzIconModule,
    AtomModule,
    NgxMaskDirective,
    // AppleSigninComponent,
    // GoogleSigninComponent,
    // MicrosoftSigninComponent,
    // DiscordSigninComponent,
    // FacebookSigninComponent,
  ],
  providers: [provideNgxMask()],
})
export class RegisterCompleteComponent implements OnInit {
  validateForm!: FormGroup<{
    formLayout: FormControl<NzFormLayoutType>
    firstName: FormControl<string>
    lastName: FormControl<string>
    email: FormControl<string>
    country: FormControl<string>
    zip: FormControl<string>
    city: FormControl<string>
    address: FormControl<string>
    countryCode: FormControl<string>
    phone: FormControl<string>
    eula: FormControl<boolean>
  }>

  formSubmitted = false
  countryList: Country[] = []
  placeholder = 'Phone number'

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    public appService: AppService,
    private sanitizer: DomSanitizer,
    private location: Location,
    private customerService: CustomerService,
    private countryService: CountryService,
    private onboardingService: OnboardingService,
    private nzNotificationService: NzNotificationService
  ) {}

  ngOnInit() {
    this.createForm()
    this.populate()
    this.initCountryList()
  }

  initCountryList() {
    this.countryList = CountryList.getAll()
      .filter((country) => {
        return ['US', 'DE', 'CN', 'KR', 'UK', 'IN'].includes(country.code)
      })
      .map((country) => ({
        code: country.code,
        name: country.name,
        dialingCode: country.dial_code,
        flag: this.sanitizer.bypassSecurityTrustHtml(
          CountryFlagSvg[country.code as never]
        ),
      }))
  }

  getFlagByCode(code: string): SafeHtml {
    const country = this.countryList.find((country) => country.code === code)
    return this.sanitizer.bypassSecurityTrustHtml(
      CountryFlagSvg[country?.code as never]
    )
  }

  getCountryNameByCode(code: string): string {
    const country = this.countryList.find((country) => country.code === code)
    return country?.name || ''
  }

  getDialingCodeByCountryCode(code: string): string {
    const country = this.countryList.find(
      (country) => country.code.toUpperCase() === code.toUpperCase()
    )
    return country?.dialingCode || ''
  }

  createForm() {
    this.validateForm = this.formBuilder.group({
      formLayout: 'horizontal' as NzFormLayoutType,
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.email, Validators.required]],
      country: ['', [Validators.required]],
      zip: ['', [Validators.required]],
      city: ['', [Validators.required]],
      address: ['', [Validators.required]],
      // dial code, like +1, +44, +49, etc.
      countryCode: ['', [Validators.required]],
      phone: ['', [Validators.required, phoneValidator('countryCode')]],
      eula: [true, [Validators.requiredTrue]],
    })
  }

  submitForm() {
    console.log('submitForm')

    this.validateForm.markAllAsTouched()
    this.validateForm.updateValueAndValidity()
    this.validateForm.validate()

    if (this.validateForm.valid) {
      this.customerService
        .getActiveCustomer()
        .pipe(
          mergeMap((activeCustomerResponse) => {
            return this.onboardingService.updateOnboarding({
              firstName: this.validateForm.value.firstName,
              lastName: this.validateForm.value.lastName,
              phone: this.getDialingCodeByCountryCode(this.validateForm.value.countryCode as string) + this.validateForm.value.phone,
              countryCode: this.validateForm.value.country as string,
              zip: this.validateForm.value.zip as string,
              city: this.validateForm.value.city as string,
              address: this.validateForm.value.address as string,
              emailAddress: activeCustomerResponse.data.activeCustomer?.emailAddress as string,
              state: OnboardingProgressState.SMS_VERIFICATION
            })
          })
        )
        .subscribe((response) => {
          const error = response.data?.updateOnboarding as {
            errorCode?: string
            message?: string
          }
          if (error?.errorCode) {
            this.nzNotificationService.error(
              error.errorCode,
              error.message as string
            )
          } else {
            this.router.navigate(['/verify'])
          }
        })
    }
  }

  unsetLastValue() {
    this.validateForm.get('phone')?.setValue('')
  }

  get f(): { [key: string]: AbstractControl } {
    return this.validateForm.controls
  }

  registerClick() {
    console.log('register click')
  }

  populate() {
    this.customerService.getActiveCustomer().subscribe(async (response) => {
      const activeCustomer = response?.data?.activeCustomer

      if (!activeCustomer) {
        return
      }

      this.validateForm.patchValue({
        firstName: activeCustomer?.firstName,
        lastName: activeCustomer?.lastName,
        email: activeCustomer?.emailAddress,
        phone: activeCustomer?.phoneNumber?.replace(
          '+' +
            parsePhoneNumberFromString(activeCustomer?.phoneNumber || '')
              ?.countryCallingCode || '',
          ''
        ),
        countryCode:
          parsePhoneNumberFromString(activeCustomer?.phoneNumber || '')
            ?.country || '',
      })

      this.onboardingService
        .getOnboarding(activeCustomer?.id as string)
        .subscribe((response) => {
          const onboarding = response?.data?.getOnboarding

          if(onboarding?.state === OnboardingProgressState.CUSTOMER_REGISTER_3PARTY){
            this.onboardingService.updateOnboarding({
              emailAddress: activeCustomer.emailAddress,
              state: OnboardingProgressState.INFORMATION_FULFILL
            }).subscribe()
          }

          // patch the user information
          this.validateForm.patchValue({
            country: onboarding?.countryCode || '',
            zip: onboarding?.zip || '',
            city: onboarding?.city || '',
            address: onboarding?.address || '',
          })

          const missingInformation =
            !this.validateForm.value.firstName ||
            this.validateForm.value.firstName === '' ||
            !this.validateForm.value.lastName ||
            this.validateForm.value.lastName === '' ||
            !this.validateForm.value.email ||
            this.validateForm.value.email === '' ||
            !this.validateForm.value.country ||
            this.validateForm.value.country === '' ||
            !this.validateForm.value.zip ||
            this.validateForm.value.zip === '' ||
            !this.validateForm.value.city ||
            this.validateForm.value.city === '' ||
            !this.validateForm.value.address ||
            this.validateForm.value.address === '' ||
            !this.validateForm.value.phone ||
            this.validateForm.value.phone === '' ||
            !this.validateForm.value.countryCode ||
            this.validateForm.value.countryCode === ''

          if (missingInformation) {
            // mark the required fields as touched
            Object.keys(this.validateForm.controls)
              .filter((field) => field !== 'formLayout' && field !== 'eula')
              .forEach((field) => {
                const control = this.validateForm.get(field)
                control?.markAsTouched({ onlySelf: true })
              })
          } else {
            this.router.navigate([onboarding?.resolveUrl])
          }
        })
    })
  }

  getPhoneMaskForCountry(countryCode: string): string {
    if (!countryCode) {
      return '000000000000' // Default mask
    }

    const masks: { [key: string]: string } = {
      US: '(000) 000-0000', // United States: (123) 456-7890
      DE: '0000 0000000', // Germany: 0123 4567890
      CN: '000 0000 0000', // China: 123 4567 8901
      KR: '00-0000-0000', // South Korea: 10-1234-5678
      UK: '00000 000000', // United Kingdom: 01234 567890
      IN: '00000 00000', // India: 12345 67890
    }

    return masks[countryCode.toUpperCase()] || '000000000000' // Default mask if country not found
  }
}
